import { createWebHistory, createRouter } from "vue-router";
import routes from "./routes";
import appConfig from "../../app.config";
import store from "../state/store";
import axios from "axios";

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});


// Aynı anda 5 istek refresh tetiklemesin diye
let refreshPromise = null;
let subscriptionAccessPromise = null;

const configuredApiBaseUrl = (process.env.VUE_APP_API_BASE_URL || "")
  .trim()
  .replace(/\/+$/, "");

const API_BASE_URL = configuredApiBaseUrl || (
  process.env.NODE_ENV === "development"
    ? "https://localhost:7204"
    : ""
);

// 🔐 GLOBAL AUTH + ROLE GUARD (refresh destekli)
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth);
  const isAccessValid = store.getters["auth/isAccessTokenValid"];

  // ✅ PUBLIC ROUTES (login dahil)
  if (!requiresAuth) {
    // Login sayfasına gidiyorsa:
    if (to.name === "login") {
      // Access geçerliyse zaten dashboard
      if (isAccessValid) {
        return next(getDefaultRoute());
      }
      // HttpOnly refresh cookie varsa oturumu sessizce geri yükle.
      try {
        if (!refreshPromise) {
          refreshPromise = store.dispatch("auth/refresh").finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;

        if (store.getters["auth/isAccessTokenValid"])
          return next(getDefaultRoute());
      } catch (e) {
        // Cookie yoksa veya süresi dolmuşsa login ekranında kal.
        await store.dispatch("auth/logout");
      }
    }

    return next();
  }


  // ✅ Access token geçerliyse direkt geç
  if (isAccessValid) {
    return checkSubscriptionAndRoles(to, next);
  }

  // Access token bellekte yoksa veya süresi dolmuşsa HttpOnly cookie ile yenile.
  try {
    if (!refreshPromise) {
      refreshPromise = store.dispatch("auth/refresh").finally(() => {
        refreshPromise = null;
      });
    }
    await refreshPromise;

    return checkSubscriptionAndRoles(to, next);
  } catch (e) {
    await store.dispatch("auth/logout");
    return next({ name: "login", query: { redirect: to.fullPath } });
  }
});

async function checkSubscriptionAndRoles(to, next) {
  const roles = (store.getters["auth/roles"] || [])
    .map((role) => String(role).toLowerCase());

  // Sistem yöneticisi şirkete bağlı değildir. Şirket kullanıcıları ise
  // abonelik olmadan yalnızca abonelik/kupon ekranına ulaşabilir.
  if (!roles.includes("admin") && !isSubscriptionRoute(to)) {
    const canUseSystem = await getSubscriptionAccess();

    if (!canUseSystem) {
      return next({
        name: "account",
        query: { tab: "subscription" },
        replace: true
      });
    }
  }

  return checkRolesAndNext(to, next);
}

function isSubscriptionRoute(route) {
  return route.name === "account" && route.query?.tab === "subscription";
}

async function getSubscriptionAccess() {
  const cachedValue = store.getters["auth/subscriptionCanUseSystem"];
  const checkedAt = store.getters["auth/subscriptionAccessCheckedAt"] || 0;

  if (cachedValue !== null && Date.now() - checkedAt < 30000) {
    return cachedValue;
  }

  if (!subscriptionAccessPromise) {
    const token = store.state.auth?.accessToken;

    subscriptionAccessPromise = axios.get(
      `${API_BASE_URL}/api/subscriptions/me`,
      {
        timeout: 10000,
        headers: { Authorization: `Bearer ${token}` }
      }
    )
      .then(async (response) => {
        const data = response.data || {};
        const canUseSystem = Boolean(
          data.canUseSystem ?? data.CanUseSystem ?? false
        );
        await store.dispatch(
          "auth/updateSubscriptionAccess",
          canUseSystem
        );
        return canUseSystem;
      })
      .catch(async () => {
        // Durum doğrulanamıyorsa güvenli tarafta kal: uygulamayı açma.
        await store.dispatch("auth/updateSubscriptionAccess", false);
        return false;
      })
      .finally(() => {
        subscriptionAccessPromise = null;
      });
  }

  return subscriptionAccessPromise;
}


function getDefaultRoute() {
  const roles = (store.getters["auth/roles"] || [])
    .map(role => String(role).toLowerCase());

  const canUseDashboard = roles.some(role =>
    ["admin", "companyadmin", "satis"].includes(role)
  );

  if (canUseDashboard) {
    return { name: "dashboard" };
  }

  if (roles.includes("muhasebe")) {
    return { name: "invoice-list" };
  }

  return { name: "unauthorized" };
}
// role kontrolünü ayrı fonksiyona aldık (temiz)
function checkRolesAndNext(to, next) {
  const userRoles = (store.getters["auth/roles"] || [])
    .map((r) => String(r).toLowerCase());

  const requiredRoles = to.matched
    .filter((r) => r.meta?.roles?.length > 0)
    .flatMap((r) => r.meta.roles)
    .map((r) => String(r).toLowerCase());

  if (requiredRoles.length > 0) {
    const ok = requiredRoles.some((r) => userRoles.includes(r));

    if (!ok) {
      return next({ name: "unauthorized" });
    }
  }

  return next();
}

// beforeResolve aynen
router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else resolve();
      });
    }
  } catch (error) {
    return;
  }

  if (routeTo.meta?.title) {
    document.title = routeTo.meta.title + " | " + appConfig.title;
  }

  next();
});

export default router;
